import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../stores/auth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { ApiError, api } from '../../lib/api';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { Icon } from '../../components/ui/Icon';
import '../auth/auth.css';

type Mode = 'signin' | 'forgot' | 'reset';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get('resetToken') ?? '';
  const [mode, setMode] = useState<Mode>(resetToken ? 'reset' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    if (next !== 'reset' && resetToken) {
      const params = new URLSearchParams(searchParams);
      params.delete('resetToken');
      setSearchParams(params, { replace: true });
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      if (mode === 'signin') {
        await login(email, password);
        navigate('/dashboard/sites', { replace: true });
      } else if (mode === 'forgot') {
        await api.forgotPassword(email.trim());
        setNotice('If that email has an account, we have sent a link to reset the password. It works for one hour.');
      } else {
        if (password.length < 8) {
          setError('Choose a password with at least 8 characters.');
        } else if (password !== confirm) {
          setError('The two passwords do not match.');
        } else {
          await api.resetPassword({ token: resetToken, password });
          setPassword('');
          setConfirm('');
          setMode('signin');
          const params = new URLSearchParams(searchParams);
          params.delete('resetToken');
          setSearchParams(params, { replace: true });
          setNotice('Your password has been changed. You can sign in now.');
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        // Their account exists but was never confirmed: a new code is on its way, so go to the code page.
        navigate('/verify-email', { state: { email: email.trim().toLowerCase() } });
        return;
      }
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(mode === 'signin' ? 'Could not sign in. Please try again.' : 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const title = mode === 'signin' ? 'Sign in' : mode === 'forgot' ? 'Reset your password' : 'Choose a new password';

  return (
    <div className="auth-page">
      <div className="auth-topbar">
        <Link className="auth-brand" to="/">
          <span className="auth-logo-mark" aria-hidden="true"><Icon name="zap" size={16} /></span>
          <span>Sarnia Builder</span>
        </Link>
        <ThemeToggle />
      </div>
      <div className="auth-main">
        <div className="auth-form-panel">
          <div className="auth-form-card">
            <h1>{title}</h1>
            {mode === 'forgot' ? (
              <p className="auth-form-intro">Enter the email you signed up with and we will send you a reset link.</p>
            ) : null}
            <form onSubmit={handleSubmit}>
              {error ? <Alert variant="error">{error}</Alert> : null}
              {notice ? <Alert variant="success">{notice}</Alert> : null}
              {mode !== 'reset' ? (
                <Input
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              ) : null}
              {mode !== 'forgot' ? (
                <Input
                  label={mode === 'reset' ? 'New password' : 'Password'}
                  type="password"
                  autoComplete={mode === 'reset' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              ) : null}
              {mode === 'reset' ? (
                <Input
                  label="Repeat new password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              ) : null}
              <Button type="submit" loading={submitting} style={{ width: '100%' }}>
                {mode === 'signin' ? 'Sign in' : mode === 'forgot' ? 'Send reset link' : 'Save new password'}
              </Button>
              <p className="auth-form-footer">
                {mode === 'signin' ? (
                  <>
                    <button type="button" className="auth-link-button" onClick={() => switchMode('forgot')}>
                      Forgot your password?
                    </button>
                    {' · '}
                    <Link to="/signup">Create an account</Link>
                  </>
                ) : (
                  <button type="button" className="auth-link-button" onClick={() => switchMode('signin')}>
                    Back to sign in
                  </button>
                )}
              </p>
            </form>
          </div>
        </div>
        <aside className="auth-side">
          <div className="auth-side__content">
            <h2 className="auth-side__tagline">Small island. Big signal.</h2>
            <ul className="auth-side__list">
              <li className="auth-side__item"><Icon name="check" size={20} /> Visual editing, no code guesswork</li>
              <li className="auth-side__item"><Icon name="check" size={20} /> Designer templates made in Guernsey</li>
              <li className="auth-side__item"><Icon name="check" size={20} /> Free previews and one-click export</li>
            </ul>
          </div>
          <div className="auth-side__preview">
            <img src="/template-previews/design-12.jpg" alt="Sarnia Builder template preview" loading="lazy" />
          </div>
        </aside>
      </div>
    </div>
  );
}
